import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { SEO, breadcrumbSchema } from '../components/SEO';
import CTASection from '../components/CTASection';
import { Reveal } from '../components/Reveal';
import { getProject } from '../data/projects';
import { getService } from '../data/services';
import NotFoundPage from './NotFoundPage';

const ProjectPage = () => {
  const { slug } = useParams();
  const project = getProject(slug);
  if (!project) return <NotFoundPage />;
  const service = getService(project.serviceSlug);

  return (
    <>
      <SEO
        title={`${project.title} | ${project.category} | Nebraska Backyard Living`}
        description={project.summary}
        path={`/projects/${project.slug}/`}
        image={project.image.src}
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Projects', path: '/projects/' }, { name: project.title, path: `/projects/${project.slug}/` }])]}
      />
      <section className="pt-32 md:pt-40">
        <div className="container-site">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs font-medium uppercase tracking-[0.15em] text-muted2">
            <Link to="/projects/" className="transition-colors hover:text-bronze">Projects</Link>
            <span className="mx-2">/</span>
            <span className="text-ink">{project.title}</span>
          </nav>
          <Reveal>
            <p className="overline-label">{project.category}</p>
            <h1 className="mt-3 max-w-3xl font-serif text-4xl font-medium leading-[1.02] tracking-tight text-ink sm:text-5xl lg:text-6xl">{project.title}</h1>
          </Reveal>
          <Reveal delay={0.15} className="mt-10">
            <div className="img-frame aspect-[16/8] border border-line p-3">
              <img src={project.image.src} alt={project.image.alt} fetchpriority="high" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="container-site grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Reveal>
              <h2 className="font-serif text-3xl font-medium tracking-tight text-ink">Project overview</h2>
              <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted2">{project.summary}</p>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted2">
                Spaces like this begin with a master plan: how the homeowners cook, gather, and unwind, translated into materials and structures built for Nebraska seasons. Every element — from surface to shade to fire — is designed as one composition.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-4">
            <Reveal delay={0.1}>
              <div className="border border-line bg-paper2/50 p-7">
                <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">Scope of work</h3>
                <ul className="mt-5 space-y-3">
                  {project.scope.map((s) => (
                    <li key={s} className="flex items-start gap-3 text-sm text-ink/80">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-bronze" /> {s}
                    </li>
                  ))}
                </ul>
                {service && (
                  <Link to={`/services/${service.slug}/`} data-testid="project-service-link" className="link-underline mt-7 text-xs uppercase tracking-[0.2em]">
                    Explore {service.navTitle} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTASection title="Want a space like this?" text="Start with a free on-site consultation. We'll design around your property, your style, and your budget." testId="project-cta" />
    </>
  );
};

export default ProjectPage;
